import { useOverlay } from '@abyss/web/hooks/useOverlay';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../../common/AdobeTagging/adobeModalTrackEvent';
import { ChoosePCPAlert } from '../../../../common/Alert/ChoosePCPAlert';
import { CompareShareToggle } from '../../../../common/CompareShareToggle';
import { Constants } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { ResultSort } from '../../../../common/ContentFilters/ResultSort';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { CompareProvider } from '../../../../models/Provider';
import { shouldUseGehaV2Styling } from '../../../../utils/gehaThemeUtils';
import { DownloadResultsButton } from '../ViewAll/DownloadResultsButton';
import { ShareCompareButton } from '../ViewAll/ShareCompareButton';
import { ConfirmationModal } from './CompareDrawer/Components/ConfirmationModal';
import {
  FilterContainer,
  filterLabelStyle,
} from './FiltersContainerDesktop.styled';
import { FilterProviderResultsPrintViewProps } from './Print/FilterProviderResultsPrintView';

type Props = {
  heading?: string;
  sectionType: string;
  openCompare?: boolean;
  setOpenCompare: (a: boolean) => void;
  setOpenShare: (a: boolean) => void;
  setSelectedCheckbox: (a: { checked: {} }) => void;
  setSelectedItems: (a: CompareProvider[]) => void;
  setOpenBoxContents: (a: boolean) => void;
  openShare: boolean;
  search?: string | null;
  pageSize?: number;
  pageNumber?: number;
  compareShareToggle: boolean;
  setCompareShareToggle: (a: boolean) => void;
  filterProviderResultsPrintViewProps?: FilterProviderResultsPrintViewProps;
  enableMapViewHideOption?: boolean;
};

export const MapViewHeaderEnhancement = ({
  heading,
  sectionType,
  openCompare,
  setOpenCompare,
  setOpenShare,
  setSelectedCheckbox,
  setSelectedItems,
  setOpenBoxContents,
  openShare,
  search,
  pageSize,
  pageNumber,
  compareShareToggle,
  setCompareShareToggle,
  filterProviderResultsPrintViewProps,
  enableMapViewHideOption = false,
}: Props) => {
  const { t } = useTranslation();
  const modal = useOverlay('mapView-enhancement-confirmation-modal');
  const [isClicked, setIsClicked] = useState<boolean>(false);

  const [enableMapViewEnhancements, showMergedCompareAndShare] = useFeatureFlag(
    [
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
      ConstantsLagoon?.FEATURE_FLAGS?.ENABLE_COMPARE_SMART_CHOICE,
    ]
  );
  const [toggle, setToggle] = useState<boolean>(compareShareToggle);
  const translatedText = heading?.length
    ? t(`${heading}`)
    : t(`${search}`) || '';

  const hasTranslatedText = Boolean(translatedText?.trim());

  const handleCompareShareToggle = (isChecked: boolean) => {
    if (isChecked) {
      adobeLinkTrackEvent({
        name: 'Compare and share a pdf',
        location: 'Toggle',
        type: 'Internal',
      });
    } else {
      adobeModalTrackEvent('are you sure you want to exit');
      setToggle(true);
      modal?.open();
    }
  };

  useEffect(() => {
    setToggle(compareShareToggle);
  }, [compareShareToggle]);
  const handleClickCb = () => {
    setToggle(false);
    setCompareShareToggle(false);
    setOpenCompare(false);
  };

  const isGEHAV2 = shouldUseGehaV2Styling();
  const additionalIconCss = isGEHAV2 ? { color: '#00172F' } : {};

  return (
    <React.Fragment>
      <Layout.Stack
        alignItems="left"
        css={{ '@screen < $sm': { display: 'none' } }}
        grow={true}
        space={0}
      >
        <Flex
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            maxWidth: '100%',
          }}
        >
          <Flex
            css={{
              paddingBottom: '10px',
              '.abyss-text-root': {
                fontSize: '14px',
                lineHeight: '18px',
              },
              '.abyss-icon-material': {
                height: '18px',
                width: '18px',
              },
            }}
          >
            {hasTranslatedText && (
              <Heading
                css={{
                  'abyss-heading-root': {
                    fontSize: '22.78px !important',
                    lineHeight: '28px !important',
                    marginBottom: '12px',
                    width: '100%',
                  },
                }}
                data-auto-testid="results-heading"
                data-testid="results-heading"
                display="h4"
                id="map-view-results"
                offset={1}
                textAlign="left"
              >
                {translatedText}
              </Heading>
            )}
            <Layout.Group
              alignLayout="between"
              space={enableMapViewHideOption ? 8 : 15}
            >
              <Layout.Group
                alignLayout="between"
                space={enableMapViewHideOption ? 8 : 15}
                width={enableMapViewHideOption ? '462px' : undefined}
              >
                <Flex
                  alignItems="center"
                  css={{ width: '100%' }}
                  justify="space-between"
                >
                  {showMergedCompareAndShare &&
                  sectionType === Constants.RESULT_SECTION.PROVIDER ? (
                    <CompareShareToggle
                      data-testid="compare-share-toggle-map-view"
                      dataTestId="compare-share-toggle-map-view"
                      expandedProviderListView={false}
                      isChecked={toggle}
                      onChange={(e) => {
                        handleCompareShareToggle(e.target.checked);
                        if (e.target.checked) {
                          setToggle(true);
                          setCompareShareToggle(true);
                          setOpenCompare(true);
                        }
                      }}
                      title={t('COMPARE_BUTTONS.COMPARE_AND_SHARE')}
                    ></CompareShareToggle>
                  ) : (
                    <>
                      {sectionType === Constants.RESULT_SECTION.PROVIDER && (
                        <ShareCompareButton
                          enableMapViewEnhancements={enableMapViewEnhancements}
                          isShareButton={false}
                          openCompare={openCompare}
                          sectionType={sectionType}
                          setIsClicked={setIsClicked}
                          setOpenCompare={setOpenCompare}
                          setOpenShare={setOpenShare}
                          setSelectedCheckbox={setSelectedCheckbox}
                          setSelectedItems={setSelectedItems}
                          text={t('COMPARE_BUTTONS.COMPARE')}
                          view="desktop"
                        />
                      )}
                      <ShareCompareButton
                        enableMapViewEnhancements={enableMapViewEnhancements}
                        openShare={openShare}
                        sectionType={sectionType}
                        setIsClicked={setIsClicked}
                        setOpenCompare={setOpenCompare}
                        setOpenShare={setOpenShare}
                        setSelectedCheckbox={setSelectedCheckbox}
                        setSelectedItems={setSelectedItems}
                        text={t('COMPARE_BUTTONS.SHARE')}
                        view="desktop"
                      />
                    </>
                  )}
                  <FilterContainer aria-labelledby="sort-by" role="group">
                    {enableMapViewEnhancements && enableMapViewHideOption ? (
                      <Flex css={{ position: 'relative', gap: '8px' }}>
                        <div style={{ position: 'relative', top: '8px' }}>
                          <Text
                            css={{ ...filterLabelStyle, ...additionalIconCss }}
                            data-testid="sort-by-heading"
                          >
                            {t('FILTER_LABELS.SORT')}:
                          </Text>
                        </div>
                        <ResultSort
                          enableMapViewHideOption={enableMapViewHideOption}
                          hideOverflow={true}
                          sectionType={sectionType}
                        />
                      </Flex>
                    ) : (
                      <DownloadResultsButton
                        enableMapViewEnhancements={false}
                        filterProviderResultsPrintViewProps={
                          filterProviderResultsPrintViewProps
                        }
                        isClicked={isClicked}
                        pageSize={pageSize}
                        printPage={pageNumber}
                        setCompareShareToggle={setCompareShareToggle}
                        setIsClicked={setIsClicked}
                        setOpenBoxContents={setOpenBoxContents}
                        setOpenCompare={setOpenCompare}
                        setOpenShare={setOpenShare}
                        setSelectedCheckbox={setSelectedCheckbox}
                        setSelectedItems={setSelectedItems}
                        text={t('COMPARE_BUTTONS.DOWNLOAD_RESULTS')}
                        view="desktop"
                      />
                    )}
                  </FilterContainer>
                </Flex>
              </Layout.Group>
            </Layout.Group>
          </Flex>
        </Flex>
        {!enableMapViewEnhancements && <ChoosePCPAlert isMapView />}
      </Layout.Stack>
      <ConfirmationModal
        handleClickCb={handleClickCb}
        modal={modal}
        modalName={'mapView-enhancement-confirmation-modal'}
        setOpenCompare={setOpenCompare}
        setSelectedCheckbox={setSelectedCheckbox}
        setSelectedItems={setSelectedItems}
      />
    </React.Fragment>
  );
};
